import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getComicById, comics } from '@/mocks/comics';

const mockChapters = Array.from({ length: 20 }, (_, i) => ({
  id: i + 1,
  title: `第${i + 1}话`,
}));

function getRelatedComics(currentId: number, currentCategory: string) {
  return comics
    .filter(c => c.id !== currentId && c.categorySlug === currentCategory)
    .slice(0, 5);
}

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} - 香香漫画韩漫免费阅读`;
    } else {
      document.title = '漫画未找到 - 香香漫画';
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id, comic]);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl text-foreground-800 mb-3">漫画未找到</h2>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedComics = getRelatedComics(comic.id, comic.categorySlug);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    genre: comic.category,
    inLanguage: 'zh-CN',
    aggregateRating: { '@type': 'AggregateRating', ratingValue: comic.rating, bestRating: '10' },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xiangxiangmanhua.com/' },
      { '@type': 'ListItem', position: 2, name: `${comic.category}漫画`, item: `https://xiangxiangmanhua.com/category/${comic.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <nav className="px-4 md:px-6 py-4" aria-label="面包屑导航">
            <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-500 transition-colors">首页</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li>
                <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors">
                  {comic.category}漫画
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-800">{comic.title}</li>
            </ol>
          </nav>

          <article className="px-4 md:px-6 pb-14">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <div className="relative overflow-hidden rounded-lg aspect-[3/4] w-full">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} 封面 - 香香漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-3 left-3">
                    <span className={`px-2.5 py-1 rounded-md text-background-50 text-sm font-medium ${
                      comic.status === '连载中' ? 'bg-accent-500' : 'bg-secondary-500'
                    }`}>
                      {comic.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <div className="flex items-center gap-1.5">
                    <span className="text-accent-400 font-bold text-lg">{comic.rating}</span>
                    <span className="text-xs text-foreground-500">分</span>
                  </div>
                  <span className="text-foreground-400">·</span>
                  <span className="text-sm text-foreground-600">{comic.author}</span>
                  <span className="text-foreground-400">·</span>
                  <span className="text-sm text-foreground-600">{comic.category}</span>
                  <span className="text-foreground-400">·</span>
                  <span className="text-sm text-foreground-600">共 {comic.chapters} 话</span>
                  <span className="text-foreground-400">·</span>
                  <span className="text-sm text-foreground-600">
                    {(comic.views / 10000).toFixed(0)}万阅读
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-4 mb-6">
                  <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-line"></i>
                    开始阅读 第1话
                  </Link>
                  <button
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-200 text-foreground-700 text-sm hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
                    aria-label="收藏"
                  >
                    <i className="ri-bookmark-line"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>

            <section className="mt-10 md:mt-14" aria-labelledby="chapters-title">
              <h2 id="chapters-title" className="text-xl font-heading text-foreground-950 mb-5">
                章节列表
                <span className="text-sm text-foreground-500 font-normal ml-2">共 {comic.chapters} 话</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {mockChapters.slice(0, 20).map((ch) => (
                  <Link
                    key={ch.id}
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-center px-3 py-2.5 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-primary-500/10 hover:text-primary-500 transition-all cursor-pointer whitespace-nowrap"
                  >
                    {ch.title}
                  </Link>
                ))}
              </div>
              {comic.chapters > 20 && (
                <div className="text-center mt-5">
                  <button className="px-5 py-2.5 rounded-full bg-background-200 text-foreground-600 text-sm hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap">
                    查看全部 {comic.chapters} 话
                    <i className="ri-arrow-down-s-line ml-1"></i>
                  </button>
                </div>
              )}
            </section>

            <section className="mt-10 md:mt-14" aria-labelledby="related-title">
              <h2 id="related-title" className="text-xl font-heading text-foreground-950 mb-5">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-product-shop>
                {relatedComics.map((related) => (
                  <Link
                    key={related.id}
                    to={`/comic/${related.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2">
                      <img
                        src={related.cover}
                        alt={related.title}
                        title={`${related.title} - 相关推荐`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                          {related.rating}分
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm text-foreground-800 font-medium group-hover:text-primary-500 transition-colors line-clamp-1">
                      {related.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{related.author}</p>
                  </Link>
                ))}
              </div>
            </section>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}